import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { NavigationService } from '../services/navigation.service';
import { RightEnum } from '../models/rights.model';

export interface RouteDataWithRights {
  requiredRights?: RightEnum | RightEnum[];
  [key: string]: any;
}

export const authorizationWithFallbackGuard: CanActivateFn = (route, state): Observable<boolean> => {
  const authService = inject(AuthService);
  const navigationService = inject(NavigationService);
  const router = inject(Router);

  const routeData = route.data as RouteDataWithRights;
  const requiredRights = routeData.requiredRights;

  if (!requiredRights || (Array.isArray(requiredRights) && requiredRights.length === 0)) {
    return authService.getAllRights().pipe(map(() => true));
  }

  const rightsArray = Array.isArray(requiredRights) ? requiredRights : [requiredRights];

  return authService.getAllRights().pipe(
    map((userRights) => {
      const hasRequiredRights = rightsArray.some(right => userRights?.includes(right));
      if (hasRequiredRights) {
        return true;
      } else {
        const urlWithoutParams = state.url.split('?')[0];
        const isRootOrDashboard = urlWithoutParams === navigationService.ROOT_URL ||
                                   urlWithoutParams === `/${navigationService.DASHBOARD_URL}`;

        if(isRootOrDashboard) {
          const authorizedPath = getFirstAuthorizedMenu(userRights, navigationService);
          if (authorizedPath) {
            router.navigate([`/${authorizedPath}`]);
          } else {
            router.navigate([navigationService.UNAUTHORIZED]);
          }
          return false;
        } else {
          router.navigate([navigationService.UNAUTHORIZED]);
          return false;
        }
      }
    })
  );
};

function getFirstAuthorizedMenu(userRights: RightEnum[], navigationService: NavigationService) {
  const menuRights = [
    { right: RightEnum.ContreMarque, path: navigationService.DASHBOARD_URL },
    { right: RightEnum.SaleAccess, path: navigationService.SALES_URL },
    { right: RightEnum.OrderAccess, path: navigationService.ORDERS_URL },
    { right: RightEnum.CustomerAccess, path: navigationService.CUSTOMERS_URL },
    {
      right: RightEnum.CatalogAcces,
      path: `${navigationService.CATALOG_URL}/${navigationService.EVENTS_URL}`,
    },
    { right: RightEnum.ReportEditAccess, path: navigationService.RECIPE_SHEET },
    { right: RightEnum.MockupAccess, path: navigationService.SETTINGS_URL },
  ];

  return menuRights.find((menuRight) => userRights?.includes(menuRight.right))?.path;
}
